// src/views/accueil/BoutonMicro.jsx — LE BOUTON DU MICRO DE L'OPÉRATEUR.
//
// LE PANNEAU EXISTE DEPUIS LE 2026-09-06 — le bouton ne dit plus « pas encore ».
//
// ⚠️ SON CONTRAT EST ÉTROIT, ET C'EST LA MAQUETTE QUI LE DESSINE :
//   « le micro barre = raccourci qui N'AFFICHE PLUS DE PROPOSITION et NE PEUT
//     PLUS ENVOYER — exactement : il écoute, récite ce qu'il a compris,
//     emmène. »
// Trois gestes, DEUX INTERDITS. Le panneau n'a donc aucun bouton d'envoi et
// n'affiche aucun brouillon : il restitue ce qu'il a entendu, puis emmène à la
// destination, où les gardes d'envoi vivent déjà. `src/lib/dictee.js` ne sait
// ni proposer ni envoyer PAR CONSTRUCTION (surface publique verrouillée par
// test) — on ne peut pas s'en servir de travers.
//
// ⚠️ IL NE MEUBLE TOUJOURS PAS — règle du micro : quand il ne peut pas
// écouter (Firefox n'implémente pas l'API, micro refusé, panne), il LE DIT,
// et il propose quand même d'ouvrir la destination.
//
// ⚠️ OÙ IL SE POSE (arbitré 2026-08-26, après MESURE) :
//   · ORDINATEUR : flottant, en bas à droite, 20 px des bords, 62 px.
//   · TÉLÉPHONE  : DANS LA BARRE DU HAUT, 38 px, à gauche de la Déconnexion —
//     comme la maquette. C'était impossible tant que la barre du haut défilait
//     avec la page ; elle est COLLANTE depuis ce lot (une ligne, mesurée sans
//     aucun conflit de superposition sur les cinq écrans admin ni sur les deux
//     espaces validés). Il ne flotte donc plus sur téléphone, et il n'entre
//     pas dans la barre du bas — où il masquerait un onglet.
//   · ADMIN, MAQUETTE NEST v677 : au téléphone il devient le logo NEST au
//     milieu de la barre du bas (`dansBarreBas`), rond de 48 px qui dépasse ;
//     la barre du haut ne le porte plus (`ordinateurSeul`).

import { useState, useEffect, useRef, useCallback } from 'react'
import { DS } from '../../lib/ui'
import useEtroit from '../../lib/useEtroit'
import { estDisponible, ecouter, RAISONS } from '../../lib/dictee'
import { LOGO_NEST } from '../../assets/logo-nest'

const CIEL = '#87b8e6'

/** Le micro TRACÉ — rectangle arrondi, arc, pied. Blanc, sur rond plein. */
function DessinMicro({ taille }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 24 24" fill="none"
      stroke="#fff" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
    </svg>
  )
}

/** LE PANNEAU — il écoute, récite, emmène. Il n'envoie pas, il ne propose pas. */
function Panneau({ testId, style, onEmmener }) {
  const [etat, setEtat] = useState('pret')   // pret | ecoute | compris | empeche
  const [texte, setTexte] = useState('')
  const [raison, setRaison] = useState(null)
  const prise = useRef(null)

  // Couper l'écoute si le panneau disparaît — un micro qui reste ouvert après
  // la fermeture est une fuite que personne ne voit.
  useEffect(() => () => { if (prise.current) prise.current.arreter() }, [])

  const demarrer = useCallback(() => {
    if (!estDisponible()) { setRaison('absente'); setEtat('empeche'); return }
    setTexte(''); setRaison(null); setEtat('ecoute')
    prise.current = ecouter({
      onTexte: (t, definitif) => { setTexte(t); if (definitif) setEtat('compris') },
      onErreur: (r) => { setRaison(r); setEtat('empeche') },
      onFin: () => setEtat((e) => (e === 'ecoute' ? (texte ? 'compris' : 'pret') : e)),
    })
  }, [texte])

  const arreter = useCallback(() => {
    if (prise.current) prise.current.arreter()
    setEtat(texte ? 'compris' : 'pret')
  }, [texte])

  return (
    <div data-testid={testId} role="status"
      // ⚠️ UNE LARGEUR, PAS UN MAXIMUM (leçon d'écran : posé en absolu, un
      // simple maxWidth laissait la boîte se rétracter à la largeur du micro,
      // et la phrase tombait en colonne d'un mot par ligne).
      style={{ background: '#fff', border: `2px solid ${CIEL}`, borderRadius: 14,
        padding: '11px 13px', width: 'min(270px, calc(100vw - 32px))',
        fontFamily: DS.fontBody, fontSize: 11.5,
        lineHeight: 1.45, color: '#233240',
        boxShadow: '0 10px 34px rgba(35,50,64,.26)', ...style }}>

      {etat === 'pret' && (
        <>
          <div style={{ marginBottom: 9 }}>Dites-lui ce que vous voulez — il vous emmènera au bon endroit.</div>
          <button type="button" data-testid={`${testId}-parler`} onClick={demarrer}
            style={btn}>Parler</button>
        </>
      )}

      {etat === 'ecoute' && (
        <>
          <div style={{ marginBottom: 9, fontWeight: 600 }}>Il écoute…</div>
          {texte && <div data-testid={`${testId}-texte`} style={recite}>{texte}</div>}
          <button type="button" data-testid={`${testId}-stop`} onClick={arreter}
            style={btn}>J&apos;ai fini</button>
        </>
      )}

      {etat === 'compris' && (
        <>
          <div style={{ marginBottom: 6 }}>Voici ce que j&apos;ai compris :</div>
          <div data-testid={`${testId}-texte`} style={recite}>{texte || RAISONS.vide}</div>
          {/* ⚠️ AUCUN bouton d'envoi, AUCUN brouillon affiché — contrat de la
              maquette. Le seul geste possible est d'aller à la destination,
              où les gardes d'envoi vivent déjà. */}
          <button type="button" data-testid={`${testId}-emmener`} onClick={onEmmener}
            style={btn}>M&apos;y emmener</button>
        </>
      )}

      {etat === 'empeche' && (
        <>
          <div data-testid={`${testId}-empeche`} style={{ marginBottom: 9 }}>
            {RAISONS[raison] || RAISONS.panne}
          </div>
          {/* Il ne peut pas écouter — il ne fait pas semblant, et il emmène
              quand même : l'empêchement du micro n'est pas une impasse. */}
          <button type="button" data-testid={`${testId}-emmener`} onClick={onEmmener}
            style={btn}>Ouvrir la destination</button>
        </>
      )}
    </div>
  )
}

const btn = {
  border: 'none', background: CIEL, color: '#fff', borderRadius: 10,
  padding: '7px 12px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
}
const recite = {
  background: '#f4f8fc', borderRadius: 9, padding: '8px 10px',
  margin: '0 0 9px', fontStyle: 'italic', color: '#1b3a5c',
}

export default function BoutonMicro({ testId = 'accueil-bouton-micro', onEmmener, ordinateurSeul = false, dansBarreBas = false }) {
  const [phrase, setPhrase] = useState(false)
  const etroit = useEtroit()
  // Changer de format referme le panneau : il était ancré à l'autre position.
  useEffect(() => { setPhrase(false) }, [etroit])

  const bouton = (taille, dessin) => (
    <button type="button" data-testid={testId}
      aria-label="Parler à l'opérateur" aria-expanded={phrase}
      onClick={() => setPhrase((v) => !v)} className="fbz-press"
      style={{ width: taille, height: taille, borderRadius: '50%', border: 'none',
        background: CIEL, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0,
        boxShadow: etroit ? 'none' : '0 6px 20px rgba(35,50,64,.28)' }}>
      <DessinMicro taille={dessin} />
    </button>
  )

  if (etroit && ordinateurSeul) return null
  if (!etroit && dansBarreBas) return null

  // ── TÉLÉPHONE, ADMIN : le logo NEST au milieu de la barre du bas ─────────
  if (dansBarreBas) {
    return (
      <span style={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center' }}>
        <button type="button" data-testid={testId}
          aria-label="Parler à l'opérateur" aria-expanded={phrase}
          onClick={() => setPhrase((v) => !v)} className="fbz-press"
          style={{ width: 48, height: 48, marginTop: -16, borderRadius: '50%', border: '3px solid #fff',
            background: '#fff', padding: 0, cursor: 'pointer', overflow: 'hidden', flexShrink: 0,
            boxShadow: '0 6px 16px rgba(15,122,100,.35)' }}>
          <img src={LOGO_NEST} alt="" style={{ width: 42, height: 42, display: 'block' }} />
        </button>
        {phrase && (
          <Panneau testId={`${testId}-phrase`} onEmmener={onEmmener}
            style={{ position: 'absolute', bottom: 'calc(100% + 20px)', left: '50%', transform: 'translateX(-50%)', zIndex: 40 }} />
        )}
      </span>
    )
  }

  // ── TÉLÉPHONE : dans la barre du haut, qui est collante ──────────────────
  if (etroit) {
    return (
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        {bouton(38, 17)}
        {phrase && (
          // ⚠️ Il s'ouvre SOUS le micro sur téléphone (la maquette le dit).
          <Panneau testId={`${testId}-phrase`} onEmmener={onEmmener}
            style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 40 }} />
        )}
      </span>
    )
  }

  // ── ORDINATEUR : flottant, en bas à droite ───────────────────────────────
  return (
    <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 70,
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
      {/* ⚠️ Il s'ouvre AU-DESSUS du micro sur ordinateur. */}
      {phrase && <Panneau testId={`${testId}-phrase`} onEmmener={onEmmener} />}
      {bouton(62, 26)}
    </div>
  )
}
